import React from "react";
import { motion } from "framer-motion";
import { Layers, Droplet, Wrench, HandHeart } from "lucide-react";
import TankDiagram from "./TankDiagram";
import { WATER_EXPLAINED as W, CTA_TEXT } from "../data";

const ease = [0.16, 1, 0.3, 1];
const fade = (i = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.8, ease, delay: i * 0.08 } });

const Block = ({ icon: Icon, title, children, testid, i }) => (
  <motion.div data-testid={testid} className="border-t border-[#172B38] pt-8" {...fade(i)}>
    <Icon className="w-5 h-5 text-[#344C59]" strokeWidth={1.5} />
    <h3 className="font-heading font-normal text-[1.8rem] leading-tight text-[#172B38] mt-5">{title}</h3>
    {children}
  </motion.div>
);

export default function WaterExplained({ hideIntro = false }) {
  return (
    <section id="how-it-filters" data-testid="water-explained" className="section-pad">
      <div className="container-hw">
        {!hideIntro && (
          <motion.div className="max-w-3xl mb-10" {...fade()}>
            <h2 className="font-heading font-normal text-3xl md:text-4xl text-[#172B38] leading-tight">{W.heading}</h2>
            <p className="text-[#3E474B] mt-5 leading-relaxed text-[1.02rem]">{W.intro}</p>
          </motion.div>
        )}

        <div className="grid sm:grid-cols-2 gap-5">
          {W.compare.map((c, i) => (
            <motion.div key={c.title} data-testid={`water-compare-${i}`} className="border border-[#D9D6CC] bg-white p-8 md:p-10" {...fade(i)}>
              <h3 className="font-heading font-normal text-[1.7rem] text-[#172B38]">{c.title}</h3>
              <p className="text-[#3E474B] mt-2 leading-relaxed text-[0.95rem]">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div data-testid="water-tank" className="bg-white rounded-none p-7 md:p-10 mt-5 grid lg:grid-cols-2 gap-10 items-center" {...fade()}>
          <div>
            <Layers className="w-5 h-5 text-[#344C59]" strokeWidth={1.5} />
            <h3 className="font-heading font-normal text-[2.2rem] leading-tight text-[#172B38] mt-5">{W.tank.title}</h3>
            <p className="text-[#3E474B] mt-3 leading-relaxed text-[0.95rem]">{W.tank.text}</p>
            <ol className="mt-6 space-y-4">
              {W.tank.layers.map((l, i) => (
                <li key={l.name} className="flex gap-4">
                  <span className="w-7 h-7 shrink-0 bg-[#172B38] text-[#F4F1E9] text-xs flex items-center justify-center">{i + 1}</span>
                  <div><p className="font-medium text-[#172B38] text-[0.98rem]">{l.name}</p><p className="text-[#4A5357] text-sm mt-0.5 leading-relaxed">{l.text}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <TankDiagram />
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-10 mt-20">
          <Block icon={Droplet} title={W.ro.title} testid="water-ro" i={1}>
            {W.ro.paras.map((p) => <p key={p} className="text-[#3E474B] mt-3 leading-relaxed text-[0.95rem]">{p}</p>)}
          </Block>
          <Block icon={Wrench} title={W.install.title} testid="water-install" i={0}>
            <p className="text-[#3E474B] mt-3 leading-relaxed text-[0.95rem]">{W.install.text}</p>
          </Block>
          <Block icon={HandHeart} title={W.pricing.title} testid="water-pricing" i={1}>
            {W.pricing.paras.map((p) => <p key={p} className="text-[#3E474B] mt-3 leading-relaxed text-[0.95rem]">{p}</p>)}
            <a href="/#schedule" data-testid="water-explained-cta" className="btn-cta mt-6">{CTA_TEXT}</a>
          </Block>
        </div>
      </div>
    </section>
  );
}
